import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Package, Calendar, Upload, FileText, CheckCircle, Clipboard, Image, Loader2 } from 'lucide-react';
import { colors } from '../theme';

const ModalConfirmacaoEntrega = ({ isOpen, onClose, pedido, dataPreenchida, onSuccess }) => {
  const [formData, setFormData] = useState({
    data_entrega_real: '',
    comprovante_url: '',
    observacao_entrega: ''
  });
  
  // Pré-preencher data quando modal abrir
  useEffect(() => {
    if (isOpen && dataPreenchida) {
      setFormData(prev => ({
        ...prev,
        data_entrega_real: dataPreenchida
      }));
    }
  }, [isOpen, dataPreenchida]);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
  };

  // Função para processar arquivo (usada tanto por input quanto por paste)
  const processFile = async (file) => {
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Apenas arquivos JPG, PNG ou PDF são permitidos');
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 3000);
      return;
    }

    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Arquivo muito grande. Máximo 5MB');
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 3000);
      return;
    }

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API}/pedidos/${pedido.id}/upload-comprovante`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setFormData({ ...formData, comprovante_url: response.data.url });
      
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao fazer upload do arquivo';
      setErrorMessage(errorMsg);
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 4000);
    } finally {
      setUploadingFile(false);
    }
  };

  // Função para colar imagem do clipboard (Ctrl+V)
  const handlePasteComprovante = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        
        if (file) {
          const extension = item.type.split('/')[1] || 'png';
          const newFile = new File([file], `comprovante_${Date.now()}.${extension}`, {
            type: item.type
          });
          
          await processFile(newFile);
        }
        break;
      }
    }
  };

  const handleSubmit = async (e) => {
    console.log('🔵 handleSubmit chamado');
    e.preventDefault();
    
    if (!formData.data_entrega_real) {
      setErrorMessage('Data de entrega é obrigatória');
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 3000);
      return;
    }
    
    console.log('🔵 FormData:', formData);

    setLoading(true);
    
    try {
      const token = localStorage.getItem('primaza_token');
      
      // Preparar dados para envio
      const payload = {
        data_entrega_real: formData.data_entrega_real,
        comprovante_url: formData.comprovante_url || null,
        observacao_entrega: formData.observacao_entrega || null
      };
      
      console.log('Enviando confirmação de entrega:', payload);
      
      const response = await axios.post(
        `${API}/pedidos/${pedido.id}/confirmar-entrega`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Resposta:', response.data);
      
      setShowSuccessMessage(true);
      
      // Fechar modal após 2 segundos
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
        setShowSuccessMessage(false);
        
        // Reset form
        setFormData({
          data_entrega_real: '',
          comprovante_url: '',
          observacao_entrega: ''
        });
      }, 2000);
      
    } catch (error) {
      console.error('Erro completo:', error);
      console.error('Response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      setErrorMessage(`Erro ao confirmar entrega: ${errorMsg}`);
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 4000);
    } finally {
      setLoading(false);
    }
  };

  const getDataHoje = () => {
    const hoje = new Date();
    return hoje.toISOString().split('T')[0];
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    const [ano, mes, dia] = dataString.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: colors.primaza.secondary }}>
            <CheckCircle size={24} />
            Confirmar Entrega do Pedido
          </DialogTitle>
          <DialogDescription>
            Pedido: <strong>{pedido?.pedido || 'N/A'}</strong> | Cliente: <strong>{pedido?.nome_cliente || 'N/A'}</strong>
          </DialogDescription>
        </DialogHeader>

        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-sm text-blue-800">
            <strong>Importante:</strong> Informe a data REAL em que o pedido foi entregue ao cliente.
            Isso é essencial para métricas de performance das transportadoras.
          </AlertDescription>
        </Alert>

        {/* Alerta quando data vem dos Correios */}
        {dataPreenchida && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-sm text-green-800 ml-2">
              <strong>✅ Data pré-preenchida:</strong> Os Correios confirmaram a entrega nesta data. 
              Você pode alterá-la se necessário.
            </AlertDescription>
          </Alert>
        )}

        {/* Mensagem de Sucesso */}
        {showSuccessMessage && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-sm text-green-800 ml-2">
              <strong>Entrega confirmada com sucesso!</strong>
            </AlertDescription>
          </Alert>
        )}

        {/* Mensagem de Erro */}
        {showErrorMessage && (
          <Alert className="bg-red-50 border-red-200">
            <AlertDescription className="text-sm text-red-800">
              <strong>Erro:</strong> {errorMessage}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Data Real de Entrega */}
          <div>
            <Label htmlFor="data_entrega_real" className="flex items-center gap-2">
              <Calendar size={16} />
              Data Real da Entrega *
            </Label>
            <Input
              id="data_entrega_real"
              type="date"
              value={formData.data_entrega_real}
              onChange={(e) => setFormData({ ...formData, data_entrega_real: e.target.value })}
              max={getDataHoje()}
              required
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Quando o pedido foi efetivamente entregue ao cliente?
            </p>
          </div>

          {/* Upload Comprovante */}
          <div>
            <Label htmlFor="comprovante" className="flex items-center gap-2">
              <Upload size={16} />
              Comprovante de Entrega <span className="text-gray-400">(opcional)</span>
            </Label>
            
            {/* Área para colar print (Ctrl+V) */}
            <div 
              className="mt-2 border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer text-center"
              tabIndex={0}
              onPaste={handlePasteComprovante}
              onClick={(e) => e.currentTarget.focus()}
            >
              <Clipboard className="mx-auto text-blue-500 mb-2" size={28} />
              <p className="text-blue-700 font-medium text-sm">Cole aqui o print (Ctrl+V)</p>
              <p className="text-xs text-blue-600 mt-1">
                Tire um print da tela dos Correios e cole diretamente aqui
              </p>
            </div>
            
            {/* Ou selecionar arquivo */}
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-xs text-gray-500">ou</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
            
            <Input
              id="comprovante"
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={handleFileUpload}
              disabled={uploadingFile}
            />
            <p className="text-xs text-gray-500 mt-1">
              Foto do comprovante assinado, canhoto da NF, etc. (JPG, PNG ou PDF - máx 5MB)
            </p>
            
            {uploadingFile && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2">
                <Loader2 className="animate-spin h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-700">Enviando arquivo...</span>
              </div>
            )}
            
            {!uploadingFile && formData.comprovante_url && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm text-green-700">Comprovante anexado com sucesso</span>
              </div>
            )}
          </div>

          {/* Observação */}
          <div>
            <Label htmlFor="observacao_entrega" className="flex items-center gap-2">
              <FileText size={16} />
              Observações <span className="text-gray-400">(opcional)</span>
            </Label>
            <Textarea
              id="observacao_entrega"
              value={formData.observacao_entrega}
              onChange={(e) => setFormData({ ...formData, observacao_entrega: e.target.value })}
              placeholder="Ex: Cliente retirou pessoalmente, entregue ao porteiro, etc."
              rows={3}
              className="mt-1 resize-none"
            />
          </div>

          {/* Informações do Pedido */}
          <div className="p-3 bg-gray-50 rounded border space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Transportadora:</span>
              <span className="font-medium">{pedido?.transportadora || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Previsão Original:</span>
              <span className="font-medium">{formatarData(pedido?.prev_entrega)}</span>
            </div>
            {formData.data_entrega_real && pedido?.prev_entrega && (
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-600">Status Entrega:</span>
                {formData.data_entrega_real <= pedido.prev_entrega ? (
                  <span className="font-medium text-green-600">✅ No prazo</span>
                ) : (
                  <span className="font-medium text-red-600">⚠️ Com atraso</span>
                )}
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              onClick={(e) => {
                console.log('🟢 Botão clicado!', e);
              }}
              style={{
                backgroundColor: colors.primaza.primary,
                color: 'white'
              }}
            >
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Confirmando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  Confirmar Entrega
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalConfirmacaoEntrega;
